import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuditSearch, AuditSearchParams } from '../hooks/useAuditApi';
import type { AuditEntry } from '../types/domain';

import { Button, TEXT_LINK } from '../components/ui/Button';
import { Field, Input } from '../components/ui/Field';
import { FOCUS_RING_TIGHT } from '../components/ui/focus';
import { LoadError } from '../components/ui/LoadError';
import { SkeletonTable, LoadingAnnouncement } from '../components/ui/Skeleton';
import { isoSeconds } from '../lib/dates';
const PAGE_SIZE = 50;

/** The seven columns, in one place, so the skeleton cannot promise a shape the
 *  real table then contradicts (P38, task 5). */
const AUDIT_HEADERS = ['Occurred', 'Actor', 'Action', 'Entity', 'Ticket', 'Outcome', 'Correlation'];

/**
 * The date-upper-bound trap: `dateTo` in the API is an EXCLUSIVE bound over a
 * UTC `datetime2` column, but an `<input type="date">` gives back a plain
 * "YYYY-MM-DD" the user means as "through the end of that day". So the day
 * after the picked date, at UTC midnight, is what actually gets sent —
 * otherwise every row from the picked day itself would silently vanish.
 */
function exclusiveDayAfter(dateOnly: string): string {
  const d = new Date(`${dateOnly}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

interface Filters {
  dateFrom: string;
  dateTo: string;
  actor: string;
  action: string;
  entityType: string;
  ticketNumber: string;
  correlationId: string;
}

const EMPTY_FILTERS: Filters = { dateFrom: '', dateTo: '', actor: '', action: '', entityType: '', ticketNumber: '', correlationId: '' };

export default function AuditTrail() {
  // Deep link from elsewhere in the app: `/audit-trail?correlationId=…` or
  // `?ticketNumber=…` opens the page already filtered. P14's delivery-proof
  // panel links a send's correlation id here — without this the link would
  // land on an unfiltered page, which is the kind of dead end P14 removes.
  const [searchParams] = useSearchParams();
  const initial: Filters = {
    ...EMPTY_FILTERS,
    correlationId: searchParams.get('correlationId') ?? '',
    ticketNumber: searchParams.get('ticketNumber') ?? '',
  };

  const [draft, setDraft] = useState<Filters>(initial);
  const [applied, setApplied] = useState<Filters>(initial);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | number | null>(null);

  const params: AuditSearchParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: applied.dateFrom ? `${applied.dateFrom}T00:00:00.000Z` : undefined,
      dateTo: applied.dateTo ? exclusiveDayAfter(applied.dateTo) : undefined,
      actor: applied.actor || undefined,
      action: applied.action || undefined,
      entityType: applied.entityType || undefined,
      ticketNumber: applied.ticketNumber || undefined,
      correlationId: applied.correlationId || undefined,
    }),
    [applied, page]
  );

  const { data, isLoading, isError, error, errorUpdatedAt, refetch } = useAuditSearch(params);
  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  function applyFilters() {
    setPage(1);
    setApplied(draft);
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  }

  /** Clicking a CorrelationId re-filters to the whole run — that's the drill-down. */
  function showRun(correlationId: string) {
    const next = { ...EMPTY_FILTERS, correlationId };
    setDraft(next);
    setApplied(next);
    setPage(1);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink">Audit Trail</h1>
      <p className="mt-1 text-sm text-gray-500">
        Every system, user and AI action against the immutable ledger — every filter is a search, not a delete.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 rounded-card border border-hair bg-surface p-4 text-sm sm:grid-cols-4 lg:grid-cols-7">
        <Field label="From">
          {(a) => (
            <Input
              {...a} type="date"
              value={draft.dateFrom}
              onChange={e => setDraft({ ...draft, dateFrom: e.target.value })}
            />
          )}
        </Field>
        <Field label="To">
          {(a) => (
            <Input
              {...a} type="date"
              value={draft.dateTo}
              onChange={e => setDraft({ ...draft, dateTo: e.target.value })}
            />
          )}
        </Field>
        <Field label="Actor">
          {(a) => (
            <Input
              {...a} placeholder={"email or 'agent'"}
              value={draft.actor}
              onChange={e => setDraft({ ...draft, actor: e.target.value })}
            />
          )}
        </Field>
        <Field label="Action">
          {(a) => (
            <Input
              {...a} placeholder="TicketCreated"
              value={draft.action}
              onChange={e => setDraft({ ...draft, action: e.target.value })}
            />
          )}
        </Field>
        <Field label="Entity type">
          {(a) => (
            <Input
              {...a} placeholder="Ticket"
              value={draft.entityType}
              onChange={e => setDraft({ ...draft, entityType: e.target.value })}
            />
          )}
        </Field>
        <Field label="Ticket number">
          {(a) => (
            <Input
              {...a} placeholder="DRA-2026-00016"
              value={draft.ticketNumber}
              onChange={e => setDraft({ ...draft, ticketNumber: e.target.value })}
            />
          )}
        </Field>
        <Field label="Correlation ID">
          {(a) => (
            <Input
              {...a}
              value={draft.correlationId}
              onChange={e => setDraft({ ...draft, correlationId: e.target.value })}
            />
          )}
        </Field>
        <div className="col-span-2 flex items-end gap-2 sm:col-span-4 lg:col-span-7">
          <Button variant="primary" size="sm" onClick={applyFilters}>
            Search
          </Button>
          <Button variant="secondary" size="sm" onClick={clearFilters}>
            Clear
          </Button>
          {pagination && <span className="ml-auto text-xs text-gray-500">{pagination.total} row{pagination.total === 1 ? '' : 's'}</span>}
        </div>
      </div>

      {isError && <LoadError what="The audit trail" error={error} at={errorUpdatedAt} onRetry={refetch} />}
      {isLoading && (
        <>
          <LoadingAnnouncement what="the audit trail" />
          <SkeletonTable headers={AUDIT_HEADERS} rows={12} />
        </>
      )}

      {!isLoading && !isError && (
        <div className="mt-4 overflow-hidden rounded-card border border-hair bg-surface">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-head text-left text-xs font-bold uppercase tracking-wide text-muted">
                {AUDIT_HEADERS.map(h => <th key={h} className="px-3 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(entry => (
                <AuditRowPair
                  key={entry.id}
                  entry={entry}
                  expanded={expandedId === entry.id}
                  onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  onShowRun={showRun}
                />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-sm text-muted">No audit entries match these filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="mt-3 flex items-center justify-center gap-3 text-sm">
          {/* P40: the kit's secondary, which is the same outline with the house
              height, the measured disabled appearance (ground `--head`, text
              `--faint`, opacity 1 - not `opacity-40`) and the focus ring. */}
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            Previous
          </Button>
          <span className="tabular-nums text-muted">Page {pagination.page} of {pagination.totalPages}</span>
          <Button variant="secondary" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function AuditRowPair({
  entry,
  expanded,
  onToggle,
  onShowRun,
}: {
  entry: AuditEntry;
  expanded: boolean;
  onToggle: () => void;
  onShowRun: (correlationId: string) => void;
}) {
  return (
    <>
      {/* P37, audit S1-01: the row keeps its click for a mouse and stops looking
          like a control. The control is the timestamp button, which is what a
          keyboard reaches and what says whether the detail is open. */}
      <tr className="border-t border-gray-100 hover:bg-gray-50" onClick={onToggle}>
        <td className="whitespace-nowrap px-3 py-2 text-gray-500">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={`audit-detail-${entry.id}`}
            onClick={e => { e.stopPropagation(); onToggle(); }}
            title="Show the structured detail recorded with this entry"
            className={`inline-flex h-8 items-center rounded-ctl tabular-nums hover:underline ${FOCUS_RING_TIGHT}`}
          >
            {isoSeconds(entry.occurredAt)}
          </button>
        </td>
        <td className="px-3 py-2">{entry.actor}</td>
        <td className="px-3 py-2">{entry.action}</td>
        <td className="px-3 py-2 text-gray-600">{entry.entityType}{entry.entityId ? ` #${entry.entityId}` : ''}</td>
        <td className="px-3 py-2">
          {entry.ticketNumber ? (
            <Link to={`/tickets/${entry.ticketNumber}`} className={TEXT_LINK} onClick={e => e.stopPropagation()}>
              {entry.ticketNumber}
            </Link>
          ) : '—'}
        </td>
        <td className="px-3 py-2">
          <span className={
            entry.outcome === 'Success' ? 'text-green-700' : entry.outcome === 'Failure' ? 'text-red-700' : 'text-amber-700'
          }>
            {entry.outcome}
          </span>
        </td>
        <td className="px-3 py-2">
          <button
            className={`font-mono text-xs ${TEXT_LINK}`}
            onClick={e => { e.stopPropagation(); onShowRun(entry.correlationId); }}
            title="Show every row from this run"
          >
            {entry.correlationId}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr id={`audit-detail-${entry.id}`} className="border-t border-gray-100 bg-gray-50">
          <td colSpan={7} className="p-4">
            {entry.detail ? (
              <pre className="max-w-full overflow-x-auto rounded border border-gray-200 bg-white p-3 text-xs text-gray-800">
                {JSON.stringify(entry.detail, null, 2)}
              </pre>
            ) : (
              <div className="text-xs text-muted">No structured detail recorded for this entry.</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
